import { getField, updateField } from "vuex-map-fields";
import API from "@/api";
import moment from "moment";
import translation from "@/translation.json";

const initialState = () => ({
  auditItems: [],
  loadingTable: false,
  currentPage: 1,
  pagination: {
    size: 10,
    totalPages: 0,
    totalElements: 0,
    offset: 0,
  },
  loader: null,
  loadingExport: false,

  // filtros
  responseStatus: [
    "1XX: Informativo",
    "2XX: Sucesso",
    "3XX: Redirecionamento",
    "4XX: Erro do cliente",
    "5XX: Erro no servidor",
  ],
  requestTypes: ["Busca", "Criação/Envio", "Deleção", "Edição"],
  funcionalidades: [],
  loadingFuncionalidades: false,
  searchUser: "",
  selectedHttpStatus: "",
  selectedFuncionalidade: "",
  searchRequisicao: "",
  selectedStatus: "",
  searchRequestId: "",
  selectedDtRequest: [],
});

export const state = initialState();

export const getters = {
  getField,
  formatHttpMethod: () => (value) => formatHttpMethod(value),
  formatHttpStatus: () => (value) => formatHttpStatus(value),
  formatDtRequest: () => (value) => formatDtRequest(value),
  formatFuncionalidade: (state) => (value) =>
    formatFuncionalidade(value, state),
  getMethodColor: () => (method) => getMethodColor(method),
  getHttpStatusColor: () => (status) => getHttpStatusColor(status),
};

export const actions = {
  async get({ dispatch, state, commit }) {
    commit("updateField", { path: "auditItems", value: [] });
    commit("updateField", { path: "currentPage", value: 1 });

    const params = new URLSearchParams();
    params.append("page", state.currentPage - 1);
    params.append("size", state.pagination.size);
    params.append("sort", "dtRequest,DESC");

    const config = {
      params,
    };

    appendFilterParams(state, config);

    commit("updateField", { path: "loadingTable", value: true });

    let result = await API.axios.get("/audit/logs", config);

    if (!result) {
      commit("updateField", { path: "loadingTable", value: false });
      return undefined;
    } else if (result.status >= 400 && result.status <= 600) {
      commit("updateField", { path: "loadingTable", value: false });
      return result;
    } else if (result.data?.totalElements > 0) {
      commit("updateField", { path: "currentPage", value: 1 });
    } else {
      commit("updateField", { path: "currentPage", value: 0 });
      commit("updateField", { path: "pagination.totalPages", value: 0 });
      commit("updateField", { path: "pagination.offset", value: 0 });
      commit("updateField", {
        path: "pagination.totalElements",
        value: 0,
      });
    }

    // total number of items
    if (result.data.totalElements) {
      commit("updateField", {
        path: "pagination.totalElements",
        value: result.data.totalElements,
      });
      commit("updateField", {
        path: "pagination.totalPages",
        value: result.data.totalPages,
      });
      commit("updateField", {
        path: "pagination.offset",
        value: 0,
      });
    }

    if (result.data.content) {
      commit("updateField", { path: "auditItems", value: result.data.content });
    }

    commit("updateField", { path: "loadingTable", value: false });
    return result;
  },

  async getPage({ state, commit }) {
    const params = new URLSearchParams();
    params.append("page", state.currentPage - 1);
    params.append("size", state.pagination.size);
    params.append("sort", "dtRequest,DESC");

    const config = {
      params: params,
    };

    appendFilterParams(state, config);

    commit("updateField", { path: "loadingTable", value: true });

    const result = await API.axios.get("/audit/logs", config);

    if (!result) {
      commit("updateField", { path: "loadingTable", value: false });
      return undefined;
    } else if (result.status >= 400 && result.status <= 600) {
      commit("updateField", { path: "loadingTable", value: false });
      return result;
    } else if (result.data?.content) {
      // update table items
      commit("updateField", { path: "auditItems", value: result.data.content });
      commit("updateField", {
        path: "pagination.offset",
        value: result.data.pageable.offset,
      });
    }
    commit("updateField", { path: "loadingTable", value: false });
    return result;
  },

  async download({ dispatch, state, commit }) {
    const params = new URLSearchParams();
    params.append("sort", "dtRequest,DESC");

    const config = {
      params: params,
      responseType: "blob",
    };

    appendFilterParams(state, config);

    commit("updateField", { path: "loader", value: "loadingExport" });
    commit("updateField", { path: "loadingExport", value: true });

    const response = await API.axios
      .get("/audit/logs" + "/export", config)
      .then((response) => {
        commit("updateField", { path: "loadingExport", value: false });

        if (response && response.status === 200) {
          let date = moment().format("YYYY-MM-DD");
          let fileName = "DataSec_Export_" + date + ".xlsx";

          const downloadUrl = window.URL.createObjectURL(
            new Blob([response.data])
          );
          const link = document.createElement("a");

          link.href = downloadUrl;
          link.setAttribute("download", fileName);
          document.body.appendChild(link);

          link.click();
        }
        return response;
      });
    return response;
  },

  async getFuncionalidades({ dispatch, state, commit }) {
    commit("updateField", { path: "loadingFuncionalidades", value: true });
    const result = await API.axios.get("/audit/funcionalidades");
    if (!result) {
      commit("updateField", { path: "loadingFuncionalidades", value: false });
      return undefined;
    } else if (result.status >= 400 && result.status <= 600) {
      commit("updateField", { path: "loadingFuncionalidades", value: false });
      return result;
    } else if (result.data) {
      commit("updateField", { path: "funcionalidades", value: result.data });
    }
    commit("updateField", { path: "loadingFuncionalidades", value: false });
    return result;
  },

  resetState({ commit }) {
    commit("resetState");
  },
};

export const mutations = {
  updateField,
  setItemsPerPage(state, value) {
    state.pagination.size = value;
  },
  resetState(state) {
    Object.assign(state, initialState());
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};

function appendFilterParams(state, config) {
  if (state.searchUser && state.searchUser !== "") {
    config.params.append("username", state.searchUser);
  }

  if (state.selectedHttpStatus && state.selectedHttpStatus !== "") {
    config.params.append("httpMethod", state.selectedHttpStatus);
  }

  if (state.searchRequisicao && state.searchRequisicao !== "") {
    config.params.append("queryStringOrRequestBody", state.searchRequisicao);
  }

  if (state.selectedFuncionalidade && state.selectedFuncionalidade !== "") {
    config.params.append("requestUri", state.selectedFuncionalidade);
  }

  if (state.selectedStatus && state.selectedStatus !== "") {
    config.params.append("httpStatus", state.selectedStatus);
  }
  if (state.selectedDtRequest) {
    if (state.selectedDtRequest.length === 1) {
      config.params.append("initDtRequest", state.selectedDtRequest[0]);
    } else if (state.selectedDtRequest.length === 2) {
      config.params.append("initDtRequest", state.selectedDtRequest[0]);
      config.params.append("fimDtRequest", state.selectedDtRequest[1]);
    }
  }

  if (state.searchRequestId) {
    config.params.append("requestId", state.searchRequestId);
  }
}

function getMethodColor(method) {
  switch (method) {
    case "GET":
      return "color: #4CAF50";
    case "POST":
      return "color: #FF9800";
    case "PUT":
      return "color: #2196F3";
    case "DELETE":
      return "color: #F44336";
    case "OPTIONS":
      return "color: #9E9E9E";
    default:
      return "color: #000";
  }
}

function getHttpStatusColor(status) {
  if (status >= 200 && status < 300) {
    return "#4CAF50";
  } else if (status >= 300 && status < 400) {
    return "#FF9800";
  } else if (status >= 400 && status < 500) {
    return "#F44336";
  } else if (status >= 500 && status < 600) {
    return "#F44336";
  } else {
    return "#000";
  }
}

function formatHttpMethod(value) {
  if (translation.httpMethod[value])
    return `<span>${
      translation.httpMethod[value].description
    } - <span style="${getMethodColor(value)}">${value}</span></span>`;
  return value;
}

function formatHttpStatus(value) {
  if (translation.httpStatus[value])
    return translation.httpStatus[value].description;
  return value;
}

function formatDtRequest(value) {
  return moment(value).format("DD/MM/YYYY HH:mm:ss");
}

function formatFuncionalidade(item, state) {
  let currentMostSimilarFunctionality = null;

  state.funcionalidades.forEach((funcionalidade) => {
    const pattern = funcionalidade.requestUri.replace(/%/g, ".+");
    const regex = new RegExp(`.*${pattern}.*`);

    if (regex.test(item)) {
      if (currentMostSimilarFunctionality === null)
        currentMostSimilarFunctionality = funcionalidade;
      else if (
        // Deve prevalecer a rota mais profunda, ou seja, aquela que possui mais separadores "/"
        currentMostSimilarFunctionality.requestUri.split("/").length <
        funcionalidade.requestUri.split("/").length
      )
        currentMostSimilarFunctionality = funcionalidade;
    }
  });

  return currentMostSimilarFunctionality?.label || item;
}

// eslint-disable-next-line no-unused-vars
function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}
